import { AppBar, Grid, Tab, Tabs, Typography } from '@mui/material';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ApiMasterDataField } from '../../api/api-interfaces';
import {
  formatApiMasterDataProperty,
  formatDataFieldWithDataType,
} from '../../utilFunctions/formatters';
import { uuid } from '../../utilFunctions/utils';
import { getAliasForDataField } from '../../v2/api/alias/utils';
import { useEmployeeProfile } from '../../v2/api/employee/hooks';
import { EmployeeId } from '../../v2/api/employee/types';
import { DataValue } from '../../v2/api/types';
import { BetterSuspense } from '../../v2/common/components/BetterSuspense';
import { LoadingCircle } from '../../v2/common/components/LoadingCircle';
import { JapaneseEnterpriseClientDomains } from '../../v2/constants';
import {
  useAliasesContext,
  useDomainContext,
  useGlobalLocaleContext,
  useLatestEmployeeVersionContext,
  useLatestPreferencesContext,
} from '../../v2/context/contexts';
import {
  DataFieldWithDataType,
  DataTypes,
  EmployeeDataFields,
} from '../../v2/types';
import { Modal } from '../utils/Modal';

const EmployeeDataLine = styled(Grid)`
  height: 35vh;
  padding: 16px;
` as typeof Grid;

const StyledTab = styled(Tab)`
  text-transform: none;
` as typeof Tab;

interface EmployeeProfileSection {
  displayName: string;
  description: string;
  query: { dataType: DataTypes; dimensions: DataFieldWithDataType[] };
}

const standardEmployeeProfileConfig: EmployeeProfileSection[] = [
  {
    displayName: 'common:employeeProfile.personalDetails',
    description: 'Personal Details',
    query: {
      dataType: DataTypes.EMPLOYEE,
      dimensions: [
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.FULL_NAME,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.EMPLOYEE_ID,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.JOINING_AGE_GROUP,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.LOCATION_LEVEL_1,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.LOCATION_LEVEL_2,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.LOCATION_LEVEL_3,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.ORGANIZATION_LEVEL_1,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.ORGANIZATION_LEVEL_2,
        },
      ],
    },
  },
  {
    displayName: 'common:employeeProfile.jobInformationDetails',
    description: 'Job Information Details',
    query: {
      dataType: DataTypes.EMPLOYEE,
      dimensions: [
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.JOB_TITLE,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.JOB_GRADE_LEVEL_1,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.JOB_GRADE_LEVEL_2,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.JOB_GRADE_LEVEL_3,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.START_DATE,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.TERM_DATE,
        },
      ],
    },
  },
  {
    displayName: 'common:employeeProfile.compensationDetails',
    description: 'Compensation Details',
    query: {
      dataType: DataTypes.EMPLOYEE,
      dimensions: [
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.BASE_SALARY,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.BASE_SALARY_CURRENCY,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.BASE_SALARY_FREQUENCY,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.BASE_SALARY_PER_YEAR,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.ANNUAL_CASH_BONUS_1,
        },
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.ANNUAL_CASH_BONUS_1_CURRENCY,
        },
        { dataType: DataTypes.EMPLOYEE, dataField: EmployeeDataFields.ICR },
      ],
    },
  },
  {
    displayName: 'common:employeeProfile.performanceDetails',
    description: 'Performance Details',
    query: {
      dataType: DataTypes.EMPLOYEE,
      dimensions: [
        {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.PERFORMANCE_RATING,
        },
      ],
    },
  },
];

const EmployeeProfileTabs = (props: { employeeId: EmployeeId }) => {
  const { t } = useTranslation();
  const { employeeId } = props;
  const latestEmployeeVersion = useLatestEmployeeVersionContext();
  const domain = useDomainContext();
  const latestPreferences = useLatestPreferencesContext();
  const referenceCurrency = latestPreferences.referenceCurrency;

  const [accordianPosition, setAccordianPosition] = useState<number>(0);

  interface Replacement {
    oldDimension: DataFieldWithDataType;
    newDimension: DataFieldWithDataType;
  }

  const replacements: Replacement[] = [
    {
      oldDimension: {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.FULL_NAME,
      },
      newDimension: {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.LOCAL_FULL_NAME,
      },
    },
  ];

  const replaceDimensions: (
    dimensions: DataFieldWithDataType[]
  ) => DataFieldWithDataType[] = (dimensions: DataFieldWithDataType[]) =>
    dimensions.map((d) => {
      const replacement = replacements.find(
        (r) =>
          r.oldDimension.dataType === d.dataType &&
          r.oldDimension.dataField === d.dataField
      );
      return replacement?.newDimension ?? d;
    });
  const isJapaneseEnterpriseClient =
    JapaneseEnterpriseClientDomains.includes(domain);
  const finalStandardEmployeeProfileConfig: EmployeeProfileSection[] =
    isJapaneseEnterpriseClient
      ? standardEmployeeProfileConfig.map((s) => ({
          ...s,
          query: {
            ...s.query,
            dimensions: replaceDimensions(s.query.dimensions ?? []),
          },
        }))
      : standardEmployeeProfileConfig;

  const { data: employeeProfileData } = useEmployeeProfile(
    employeeId,
    latestEmployeeVersion,
    finalStandardEmployeeProfileConfig.flatMap((c) => c.query.dimensions),
    (field, value) =>
      value !== undefined && value !== null
        ? formatDataFieldWithDataType(field, value, { referenceCurrency })
        : null
  );
  const tabData: {
    dimensions: (DataFieldWithDataType & { value: DataValue })[];
  }[] = finalStandardEmployeeProfileConfig.map((c) => {
    return {
      dimensions: c.query.dimensions.flatMap((d) => {
        const value =
          employeeProfileData[
            d.dataField.toCamel() as keyof typeof employeeProfileData
          ] ?? null;
        if (value === undefined || value === null) {
          return [];
        }
        return [
          {
            ...d,
            property: d.dataField as ApiMasterDataField,
            value,
          },
        ];
      }),
    };
  });

  return (
    <>
      <AppBar position="static">
        <Tabs
          value={accordianPosition}
          onChange={(_1, value: number) => setAccordianPosition(value)}
          variant="fullWidth"
        >
          {finalStandardEmployeeProfileConfig.map((r) => (
            <StyledTab key={r.displayName} label={t(r.displayName)} />
          ))}
        </Tabs>
      </AppBar>
      <TabData dimensions={tabData[accordianPosition].dimensions} />
    </>
  );
};

const TabData = (props: {
  dimensions: (DataFieldWithDataType & { value: DataValue })[];
}) => {
  const aliases = useAliasesContext();
  const locale = useGlobalLocaleContext();
  const getAlias = getAliasForDataField(aliases, locale.selected);
  const { dimensions } = props;
  return (
    <EmployeeDataLine key={uuid()} container item xs={12}>
      {dimensions.map((dim) => {
        const value = dim.value as string;
        if (value === undefined) {
          return null;
        }
        return (
          <Grid item xs={3} key={dim.dataField}>
            <Typography variant="subtitle2">
              {formatApiMasterDataProperty(
                {
                  property: dim,
                },
                getAlias(dim)
              )}
            </Typography>
            <Typography variant="h5">{value}</Typography>
          </Grid>
        );
      })}
    </EmployeeDataLine>
  );
};

interface EmployeeProfileProps {
  onClose: () => void;
  employeeId: string;
}

export const EmployeeProfile = observer((props: EmployeeProfileProps) => {
  const { t } = useTranslation();
  const { onClose, employeeId } = props;

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={t('common:employeeProfile.title')}
    >
      <BetterSuspense fallback={<LoadingCircle color="primary" />}>
        <EmployeeProfileTabs employeeId={employeeId} />
      </BetterSuspense>
    </Modal>
  );
});
